import { memo, forwardRef } from 'react';
import { LocationType } from '@/types/firestore';
import { Store, Utensils, Beer, Building2 } from 'lucide-react';

interface TypeDisplayIconProps {
  type?: LocationType;
  size?: number; // For the icon itself
  containerSize?: number; // For the surrounding box
  isVisited?: boolean; // Added isVisited prop
  // Allow any other props that might be passed by Radix Slot, e.g., className, style
  [key: string]: any;
}

const getTypeEmojiAndColor = (type?: LocationType) => {
  switch (type) {
    case 'shop':
      return { icon: Store, color: 'bg-blue-500', emojiChar: '🛒' }; // Keep descriptive color for now, marker uses chart-1/3
    case 'restaurant':
      return { icon: Utensils, color: 'bg-green-500', emojiChar: '🍽️' };
    case 'brewery':
      return { icon: Beer, color: 'bg-yellow-500', emojiChar: '🍺' };
    case 'other':
      return { icon: Building2, color: 'bg-gray-500', emojiChar: '📌' };
    default:
      return { icon: Building2, color: 'bg-gray-500', emojiChar: '❔' };
  }
};

// Component implementation
const TypeDisplayIconInner = forwardRef<HTMLDivElement, TypeDisplayIconProps>((
  { type, size = 20, containerSize = 58, isVisited = false, className, ...props }, 
  ref
) => {
  const { emojiChar } = getTypeEmojiAndColor(type);
  const backgroundColor = isVisited ? 'var(--chart-3)' : 'var(--chart-1)';

  return (
    <div
      ref={ref} // Apply the forwarded ref
      className={`flex items-center justify-center rounded-lg shadow-sm ${className || ''}`}
      style={{
        width: `${containerSize}px`,
        height: `${containerSize}px`,
        backgroundColor: backgroundColor, 
        border: '2px solid white', // Mimicking LocationMarker border
        boxSizing: 'border-box', // Ensure border is included in width/height
        ...props.style // Merge any passed styles
      }}
      title={type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Select Type'}
      {...props} // Spread other props
    >
      <span style={{ fontSize: `${size}px` }}>
        {emojiChar}
      </span>
    </div>
  );
});

TypeDisplayIconInner.displayName = 'TypeDisplayIconInner';

// Memoized and ref-forwarding component for export
export const TypeDisplayIcon = memo(TypeDisplayIconInner);

// Set displayName on the exported component for better debugging
TypeDisplayIcon.displayName = 'TypeDisplayIcon'; 